import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { MIN_AGE_REQUIRED, PARENT_MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../lib/types/enums/common/gender.enum'
import { AccomodationTypeEnum } from '../../../lib/types/enums/staff-application/accomodation-type.enum'
import { ConveyanceTypeEnum } from '../../../lib/types/enums/staff-application/conveyance-type.enum'
import { MaritalStatusEnum } from '../../../lib/types/enums/staff-application/marital-status.enum'
import { PositionStatusEnum } from '../../../lib/types/enums/staff-application/position-status.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { HostFamilyChildInput } from '../../../lib/types/models/host-family-child/host-family-child-input'
import { HostFamilyRelativeInput } from '../../../lib/types/models/host-family-relative/host-family-relative-input'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const hostFamilyChildFormSchema: ZodType<HostFamilyChildInput> = z.object({
  gender: createEnumSchema({
    enumType: GenderEnum,
    fieldName: 'gender'
  }),
  firstName: createStringSchema('first name', { language: 'en' }),
  lastName: createStringSchema('last name', { language: 'en' }),
  birthDate: createDateStringSchema({ inputName: 'date of birth' })
})

export const hostFamilyRelativeFormSchema: ZodType<HostFamilyRelativeInput> = z.object({
  gender: createEnumSchema({
    enumType: GenderEnum,
    fieldName: 'gender'
  }),
  firstName: createStringSchema('first name', { language: 'en' }),
  lastName: createStringSchema('last name', { language: 'en' }),
  birthDate: createDateStringSchema({
    inputName: 'date of birth',
    maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED)),
    errorMessage: `The minimum required age is ${MIN_AGE_REQUIRED} years`
  }),
  relationToFamily: createStringSchema('relation to family', { language: 'en' })
})

export const hostFamilyFormSchema = (
  language: AvailableLanguages,
  t: TFunction<AvailableLanguages, undefined>
): ZodType<Omit<HostFamilyInput, 'createdAt' | 'updatedAt'>> =>
  z
    .object({
      familyName: createStringSchema("host's last name", { language }),
      parent1FirstName: createStringSchema("wife's first name", { language }),
      parent1LastName: createStringSchema("wife's last name", { language }),
      parent1PhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
      parent1DateOfBirth: createDateStringSchema({
        inputName: "wife's date of birth",
        maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(PARENT_MIN_AGE_REQUIRED)),
        errorMessage: `The minimum required age is ${PARENT_MIN_AGE_REQUIRED} years`
      }),
      parent1Email: createStringSchema("wife's email", { isEmail: true }),
      parent1Position: createStringSchema("wife's profession", { language }),
      parent1PositionStatus: createEnumSchema({
        enumType: PositionStatusEnum,
        fieldName: "wife's profession status"
      }),
      parent1Nationality: createStringSchema("wife's nationality", { language }),
      parent2FirstName: createStringSchema("husband's first name", { nullish: true }),
      parent2LastName: createStringSchema("husband's last name", { nullish: true }),
      parent2PhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
      parent2DateOfBirth: createDateStringSchema({
        inputName: "husband's date of birth",
        maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(PARENT_MIN_AGE_REQUIRED)),
        errorMessage: `The minimum required age is ${PARENT_MIN_AGE_REQUIRED} years`
      }).nullish(),
      parent2Email: createStringSchema("husband's email", { isEmail: true, nullish: true }),
      parent2Position: createStringSchema("husband's profession", { nullish: true }),
      parent2PositionStatus: createEnumSchema({
        enumType: PositionStatusEnum,
        fieldName: "husband's profession status",
        nullish: true
      }),
      parent2Nationality: createStringSchema("husband's nationality", { nullish: true }),
      maritalStatus: createEnumSchema({
        enumType: MaritalStatusEnum,
        fieldName: 'marital status'
      }),
      accomodationType: createEnumSchema({
        enumType: AccomodationTypeEnum,
        fieldName: 'accomodation type'
      }),
      numberOfBedrooms: createNumberSchema('number of bedrooms', { isPositive: true }),
      hasPets: z.boolean({ message: 'please select if you have pets' }),
      withGarden: z.boolean({ message: 'please select if you have garden' }),
      petDescription: createStringSchema('pet description', {
        nullish: true
      }),
      transportToSchool: createEnumSchema({
        enumType: ConveyanceTypeEnum,
        fieldName: 'school transport'
      }),
      trainStation: createStringSchema('train station', { nullish: true }),
      timeToSchool: createNumberSchema('Travel time to the teaching hall / school (minutes)'),
      pickupPoint: createStringSchema('pickup point', { nullish: true }),
      hobbies: createStringSchema('hobbies', { nullish: true }),
      otherInformation: createStringSchema('other Information', { nullish: true }),
      dateOfVisit: createDateStringSchema({ inputName: 'date of HF visits' }),
      visitedBy: createStringSchema('person who visited', { language }),
      isSharedRoom: z.boolean({ message: 'please select if you have shared room' }),
      roommateName: createStringSchema('roommate name', { nullish: true }),
      studentAccomodationComment: createStringSchema('student comment', { nullish: true }),
      address1: createStringSchema('address', { language }),
      address2: createStringSchema('complementary address', { nullish: true }),
      zipCode: createStringSchema('zip code', { language }),
      city: createStringSchema('city', { language }),
      country: createStringSchema('country', { language }),
      localRepresentativeFirstName: createStringSchema('local representative first name', {
        nullish: true
      }),
      localRepresentativeLastName: createStringSchema('local representative last name', {
        nullish: true
      }),
      localRepresentativePhone: PHONE_NUMBER_SCHEMA(t).nullish(),
      localRepresentativeEmail: createStringSchema('local representative e-mail', {
        isEmail: true,
        nullish: true
      }),
      children: z.array(hostFamilyChildFormSchema).optional(),
      relatives: z.array(hostFamilyRelativeFormSchema).optional(),
      picturesAndVideos: FILE_VALIDATION().array().default([])
    })
    .superRefine((values, ctx) => {
      if (!values.hasPets) return

      const petDetails = values.petDescription?.trim() ?? ''

      if (!petDetails || !/\d/.test(petDetails) || !/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(petDetails)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['petDescription'],
          message: 'type of pets and number of pets are required'
        })
      }
    })
